import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { B2bFleetDelivery } from '../../../models/Contract';
import { ContractsService } from '../../../services/contracts.service';
import { ConfirmationService } from '../../../shared/services/confirmation.service';
import { ErrorService } from '../../../shared/services/error.service';
import { TableAction } from '../../../shared/components/table/table.component';
import { exportB2bTableToExcel } from '../../../utils/b2b-table-export.util';

@Component({
  selector: 'app-b2b-fleet-deliveries',
  templateUrl: './b2b-fleet-deliveries.component.html',
  styleUrls: ['../shared/b2b-operational-shared.css', './b2b-fleet-deliveries.component.css']
})
export class B2bFleetDeliveriesComponent implements OnInit {
  contractId: number | null = null;
  contractNumber = '';
  contractName = '';
  items: B2bFleetDelivery[] = [];
  isLoading = false;
  submitting = false;
  showModal = false;
  viewMode = false;
  editingId: number | null = null;
  form!: FormGroup;

  columns = [
    { titulo: 'Região', label: 'region' },
    { titulo: 'Polo', label: 'pole' },
    { titulo: 'Placas', label: 'plates' },
    { titulo: 'Saída', label: 'departureDate', formatter: (v: any) => this.formatDate(v) },
    { titulo: 'Caminhão', label: 'truck' },
    { titulo: 'Qtd motos', label: 'motorcycleCount' },
    { titulo: 'Endereço', label: 'address' }
  ];

  actions: TableAction[] = [
    { label: 'Visualizar', icon: 'fas fa-eye', action: 'view', class: 'btn-view' },
    { label: 'Editar', icon: 'fas fa-edit', action: 'edit', class: 'btn-edit' },
    { label: 'Excluir', icon: 'fas fa-trash', action: 'delete', class: 'btn-delete' }
  ];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private fb: FormBuilder,
    private contractsService: ContractsService,
    private errorService: ErrorService,
    private confirmationService: ConfirmationService
  ) {}

  ngOnInit(): void {
    this.form = this.fb.group({
      region: [''],
      pole: [''],
      plates: [''],
      departureDate: [''],
      truck: [''],
      address: [''],
      motorcycleCount: [null],
      notes: ['']
    });

    this.route.params.subscribe(params => {
      this.contractId = +params['id'];
      if (this.contractId) {
        this.loadContractInfo();
        this.loadItems();
      }
    });
  }

  get modalTitle(): string {
    if (this.viewMode) return 'Visualizar entrega';
    return this.editingId ? 'Editar entrega' : 'Nova entrega';
  }

  loadContractInfo(): void {
    if (!this.contractId) return;
    this.contractsService.getB2bContractById(this.contractId).subscribe({
      next: (c) => {
        this.contractNumber = c.contractNumber || '';
        this.contractName = c.contractName || '';
      },
      error: (err) => this.errorService.handleHttpError(err)
    });
  }

  loadItems(): void {
    if (!this.contractId) return;
    this.isLoading = true;
    this.contractsService.getB2bFleetDeliveries(this.contractId).subscribe({
      next: (list) => {
        this.items = (list || []).map(x => this.normalize(x));
        this.isLoading = false;
      },
      error: () => { this.isLoading = false; }
    });
  }

  openCreate(): void {
    this.viewMode = false;
    this.editingId = null;
    this.form.enable();
    this.form.reset({
      region: '',
      pole: '',
      plates: '',
      departureDate: '',
      truck: '',
      address: '',
      motorcycleCount: null,
      notes: ''
    });
    this.showModal = true;
  }

  openView(item: B2bFleetDelivery): void {
    this.patchForm(item);
    this.viewMode = true;
    this.form.disable();
    this.showModal = true;
  }

  openEdit(item: B2bFleetDelivery): void {
    this.patchForm(item);
    this.viewMode = false;
    this.form.enable();
    this.showModal = true;
  }

  private patchForm(item: B2bFleetDelivery): void {
    this.editingId = item.id ?? null;
    this.form.patchValue({
      region: item.region || '',
      pole: item.pole || '',
      plates: item.plates || '',
      departureDate: this.toDateInput(item.departureDate),
      truck: item.truck || '',
      address: item.address || '',
      motorcycleCount: item.motorcycleCount ?? null,
      notes: item.notes || ''
    });
  }

  closeModal(): void {
    this.showModal = false;
    this.viewMode = false;
    this.editingId = null;
    this.form.enable();
  }

  submit(): void {
    if (this.viewMode || !this.contractId || this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    const raw = this.form.getRawValue();
    const payload: B2bFleetDelivery = {
      b2bContractId: this.contractId,
      region: raw.region || null,
      pole: raw.pole || null,
      plates: raw.plates || null,
      departureDate: raw.departureDate ? new Date(raw.departureDate).toISOString() : null,
      truck: raw.truck || null,
      address: raw.address || null,
      motorcycleCount: raw.motorcycleCount != null && raw.motorcycleCount !== '' ? Number(raw.motorcycleCount) : null,
      notes: raw.notes || null
    };

    this.submitting = true;
    const req$ = this.editingId
      ? this.contractsService.updateB2bFleetDelivery(this.contractId, this.editingId, { ...payload, id: this.editingId })
      : this.contractsService.createB2bFleetDelivery(this.contractId, payload);

    req$.subscribe({
      next: () => {
        this.submitting = false;
        this.errorService.showSuccessMessage(this.editingId ? 'Entrega atualizada.' : 'Entrega criada.');
        this.closeModal();
        this.loadItems();
      },
      error: () => { this.submitting = false; }
    });
  }

  onActionClick(event: { action: string; item: B2bFleetDelivery }): void {
    if (event.action === 'view') this.openView(event.item);
    if (event.action === 'edit') this.openEdit(event.item);
    if (event.action === 'delete') this.deleteItem(event.item);
  }

  deleteItem(item: B2bFleetDelivery): void {
    if (!this.contractId || !item.id) return;
    this.confirmationService.confirmDelete(item.region || item.plates || 'esta entrega', 'a entrega').subscribe({
      next: (ok) => {
        if (!ok) return;
        this.contractsService.deleteB2bFleetDelivery(this.contractId!, item.id!).subscribe({
          next: () => {
            this.errorService.showSuccessMessage('Entrega excluída.');
            this.loadItems();
          }
        });
      }
    });
  }


  exportExcel(): void {
    exportB2bTableToExcel(
      `b2b-entregas-${this.contractNumber || this.contractId}`,
      this.columns,
      this.items,
      'Entregas'
    );
  }

  navigateBack(): void {
    if (this.contractId) this.router.navigate(['/b2b/contracts', this.contractId, 'view']);
    else this.router.navigate(['/b2b/contracts']);
  }

  private normalize(dto: any): B2bFleetDelivery {
    return {
      id: dto.id ?? dto.Id,
      b2bContractId: dto.b2bContractId ?? dto.b2BContractId ?? dto.B2BContractId ?? this.contractId!,
      region: dto.region ?? dto.Region ?? null,
      pole: dto.pole ?? dto.Pole ?? null,
      plates: dto.plates ?? dto.Plates ?? null,
      departureDate: dto.departureDate ?? dto.DepartureDate ?? null,
      truck: dto.truck ?? dto.Truck ?? null,
      address: dto.address ?? dto.Address ?? null,
      motorcycleCount: dto.motorcycleCount ?? dto.MotorcycleCount ?? null,
      notes: dto.notes ?? dto.Notes ?? null
    };
  }

  private toDateInput(value: any): string {
    if (!value) return '';
    const d = new Date(value);
    return isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 10);
  }

  formatDate(value: any): string {
    if (!value) return '—';
    try { return new Date(value).toLocaleDateString('pt-BR'); } catch { return String(value); }
  }
}
